import React from 'react';
import { Box, Container, Typography, List, ListItem, ListItemText, useTheme, Link } from '@mui/material';
import { Scrollbars } from 'react-custom-scrollbars-2';
import config from '../config/config';

const PrivacyPolicy = () => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                width: '100%',
                py: 2,
                position: 'relative',
                overflow: 'hidden',
                flexGrow: 1,
                marginRight: -1,
            }}
        >
            {/* Background Plate */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '99%',
                    height: '400px',
                    borderRadius: '0 0 16px 16px',
                    boxShadow: '0px 20px 50px rgba(0, 0, 0, 0.2)',
                    zIndex: 0,
                }}
            ></Box>

            <Scrollbars>
                <Container maxWidth="md"
                    sx={{
                        backgroundColor: "background.default",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: "0 0 16px 16px",
                    }}
                >
                    <Typography variant="h3" align="center" sx={{ fontWeight: 'bold', mb: 6 }}>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        Welcome to Glassyfied! Your privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal information. By using our services, you consent to the data practices described here.
                    </Typography>

                    <Section title="1. Information We Collect">
                        Glassyfied collects minimal personal information through Firebase Authentication when users sign up or log in. This may include:
                        <List>
                            <ListItem><ListItemText primary="Email address" /></ListItem>
                            <ListItem><ListItemText primary="Name (if provided)" /></ListItem>
                            <ListItem><ListItemText primary="Profile picture (optional)" /></ListItem>
                        </List>
                        In the future, we may collect additional personal information, such as payment details, for billing purposes if we introduce pricing schemes. This information will only be used for the purposes stated and will be protected according to the practices outlined in this policy.
                    </Section>


                    <Section title="2. How We Use Your Information">
                        The information we collect is solely used for authentication purposes and to provide a personalized experience for users. We do not share, sell, or otherwise distribute your personal data to third parties.
                    </Section>

                    <Section title="3. Data Security">
                        We take the security of your personal information seriously. All data is transmitted securely using industry-standard encryption methods. However, no method of data transmission over the internet is 100% secure, and we cannot guarantee absolute security.
                    </Section>

                    <Section title="4. Cookies and Tracking Technologies">
                        Glassyfied may use cookies or similar technologies to improve user experience and provide a more tailored service. These technologies help us remember your preferences and improve the functionality of the platform. You can choose to disable cookies through your browser settings.
                    </Section>

                    <Section title="5. Data Retention">
                        We retain your personal information only for as long as necessary to provide the services or as required by law. Once your account is deleted, we will erase your personal information, except for any data required to be retained for legal purposes.
                    </Section>

                    <Section title="6. Sharing of Information">
                        Glassyfied does not share, sell, or rent your personal information to third parties. Your data is used only for the purpose of authentication and personalized services on our platform.
                    </Section>

                    <Section title="7. Your Rights">
                        You have the right to access, correct, or delete your personal information at any time. If you would like to exercise these rights, please contact us using the details provided in the "Contact Information" section.
                    </Section>

                    <Section title="8. Changes to This Privacy Policy">
                        Glassyfied reserves the right to modify this Privacy Policy at any time. Any changes will be posted on this page, and your continued use of the services constitutes acceptance of the updated policy.
                    </Section>

                    <Section title="9. Contact Information">
                        If you have any questions or concerns about this Privacy Policy or how we handle your personal information, please contact us at:
                        <Typography variant="body1" sx={{ marginBottom: 2 }}>
                            Email: <Link href={`mailto:${config.supportMail}`} color="primary.dark">{config.supportMail}</Link>
                        </Typography>
                        <Typography variant="body1" sx={{ marginBottom: 2 }}>
                            Phone: <Link href={`tel:${config.phoneNumber}`} color="primary.dark">{config.phoneNumber}</Link>
                        </Typography>
                    </Section>

                    <Typography variant="body2" sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
                        This Privacy Policy was last updated on 24-Jan-2025. Please review regularly for updates.
                    </Typography>
                </Container>
            </Scrollbars>
        </Box>
    );
};

const Section = ({ title, children }) => (
    <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
            {title}
        </Typography>
        <Box variant="body1">
            {children}
        </Box>
    </Box>
);

export default PrivacyPolicy;
